import { useMutation, useQueryClient } from '@tanstack/react-query';
import { auth } from '../config/firebase';
import type { Brand } from './useBrands';
import { industries } from '../components/common/IndustrySelect';

interface UpdateBrandPayload {
  name?: string;
  description?: string;
  website?: string;
  industryId?: string;
  industry?: string;
  values?: string[];
  keywords?: string[];
  languages?: string[];
  contentTone?: string;
  voice?: string;
  uniqueSellingPoints?: string[];
  recommendedFormats?: string[];
  socialMedias?: Array<{
    platform: string;
    frequency: string;
  }>;
  themes?: string[];
  contentGaps?: string[];
  engagementTactics?: string[];
  growthOpportunities?: string[];
  summary?: {
    recap: string;
    takeaways: string[];
  };
  visualIdentity?: {
    colorPalette: {
      primary: string[];
      secondary: string[];
      accent: string[];
      neutral: string[];
      recommended?: {
        complementary: string[];
        improvements: string;
      };
    };
    typography: {
      headings: {
        h1: string;
        h2: string;
        h3: string;
      };
      body: {
        primary: string;
        secondary: string;
      };
      specialty: {
        buttons: string;
        captions: string;
        navigation: string;
      };
      fallbacks: string[];
      recommendations?: {
        improvements: string;
        hierarchy: string;
      };
    };
  };
  visualContentStrategy?: {
    colorUsage: {
      socialMediaPosts: string;
      callToActions: string;
      backgrounds: string;
      seasonalAdaptations: string;
    };
    typographyGuidelines: {
      socialMediaHeaders: string;
      bodyText: string;
      quotesTestimonials: string;
      hashtagsKeywords: string;
    };
    visualElementsToEmphasize: string[];
    designConsistencyTips: string[];
    brandExtensions: {
      socialTemplates: string;
      logoUsage: string;
      imageFilters: string;
    };
  };
  contentStrategy?: {
    themes: string[];
    contentGaps: string[];
    engagementTactics: string[];
    growthOpportunities: string[];
    recommendedFormats?: string[];
    recommendedPlatforms?: Array<{
      platform: string;
      postingFrequency: string;
    }>;
  };
  overview?: {
    summary: string;
    takeaways: string[];
  };
  goToMarketStrategy?: {
    short: string;
    targetSegments: string[];
    keyPersonaTypes: string[];
    marketScope: string;
    priorityMarkets: string[];
    businessModel: string;
    distributionType: string;
  };
}

export const useBrandMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ brandId, data }: { brandId: string; data: UpdateBrandPayload }) => {
      const token = await auth.currentUser?.getIdToken();
      if (!token) {
        throw new Error('No authentication token available');
      }

      console.log(`Updating brand ${brandId} with data:`, data);

      // Transform outgoing payload to match backend expectations
      const apiData: any = { ...data };
      if (data.industryId) {
        const selectedIndustry = industries.find(ind => ind.id === data.industryId);
        apiData.industry = selectedIndustry?.name || data.industryId;
      }

      // If socialMedias is provided, mirror it into contentStrategy.recommendedPlatforms
      // Some backend versions rely on this structure for updates as well
      if (Array.isArray(data.socialMedias)) {
        const recommendedPlatforms = data.socialMedias
          .filter((sm) => sm && sm.platform && sm.frequency)
          .map((sm) => ({ platform: sm.platform, postingFrequency: sm.frequency }));
        if (recommendedPlatforms.length > 0) {
          apiData.contentStrategy = {
            ...(apiData.contentStrategy || {}),
            recommendedPlatforms,
          };
        }
      }

      // Move top-level content strategy arrays under contentStrategy if present
      const hasContentStrategyBits =
        typeof data.themes !== 'undefined' ||
        typeof data.contentGaps !== 'undefined' ||
        typeof data.engagementTactics !== 'undefined' ||
        typeof data.growthOpportunities !== 'undefined' ||
        typeof data.recommendedFormats !== 'undefined';

      if (hasContentStrategyBits) {
        const baseCs = (data.contentStrategy || {}) as any;
        const cs: any = {};
        if (typeof baseCs.themes !== 'undefined' || typeof data.themes !== 'undefined') cs.themes = baseCs.themes ?? data.themes;
        if (typeof baseCs.contentGaps !== 'undefined' || typeof data.contentGaps !== 'undefined') cs.contentGaps = baseCs.contentGaps ?? data.contentGaps;
        if (typeof baseCs.engagementTactics !== 'undefined' || typeof data.engagementTactics !== 'undefined') cs.engagementTactics = baseCs.engagementTactics ?? data.engagementTactics;
        if (typeof baseCs.growthOpportunities !== 'undefined' || typeof data.growthOpportunities !== 'undefined') cs.growthOpportunities = baseCs.growthOpportunities ?? data.growthOpportunities;
        if (typeof baseCs.recommendedFormats !== 'undefined' || typeof data.recommendedFormats !== 'undefined') cs.recommendedFormats = baseCs.recommendedFormats ?? data.recommendedFormats;
        // Avoid sending recommendedPlatforms on update unless explicitly provided by caller
        if (typeof baseCs.recommendedPlatforms !== 'undefined') cs.recommendedPlatforms = baseCs.recommendedPlatforms;
        apiData.contentStrategy = cs;
        delete apiData.themes;
        delete apiData.contentGaps;
        delete apiData.engagementTactics;
        delete apiData.growthOpportunities;
        delete apiData.recommendedFormats;
      }

      // Move summary to overview if provided
      if (typeof data.summary !== 'undefined') {
        apiData.overview = {
          ...(data.overview || {}),
          summary: data.summary?.recap ?? '',
          takeaways: data.summary?.takeaways ?? []
        };
        delete apiData.summary;
      }

      // Sanitize visualIdentity to avoid unsupported nested props triggering 500s
      if (apiData.visualIdentity) {
        if (apiData.visualIdentity.colorPalette) {
          // Many backends don't persist recommendations server-side on update
          if (apiData.visualIdentity.colorPalette.recommended) {
            delete apiData.visualIdentity.colorPalette.recommended;
          }
        }
        if (apiData.visualIdentity.typography) {
          if (apiData.visualIdentity.typography.recommendations) {
            delete apiData.visualIdentity.typography.recommendations;
          }
        }
      }

      // Remove undefined values from root to keep payload minimal
      Object.keys(apiData).forEach((key) => {
        if (apiData[key] === undefined) delete apiData[key];
      });

      // Avoid sending empty nested objects to reduce 500 risks
      if (apiData.contentStrategy && Object.keys(apiData.contentStrategy).length === 0) {
        delete apiData.contentStrategy;
      }
      if (apiData.overview && Object.keys(apiData.overview).length === 0) {
        delete apiData.overview;
      }

      console.log('Update brand payload shaped for API:', JSON.stringify(apiData));

      try {
        const response = await fetch(`https://api.socialness.ai/management/brands/${brandId}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(apiData)
        });

        console.log('PUT response status:', response.status);

        if (!response.ok) {
          const errorText = await response.text();
          console.error('Error response from API:', errorText);
          try {
            const errorJson = JSON.parse(errorText);
            const apiMsg = errorJson.message || errorJson.error || response.statusText;
            throw new Error(`Failed to update brand (${response.status}): ${apiMsg}`);
          } catch {
            throw new Error(`Failed to update brand (${response.status}): ${errorText}`);
          }
        }

        const responseData = await response.json();
        console.log('Brand updated successfully:', responseData);
        return responseData;
      } catch (error) {
        console.error('Error in brand mutation:', error);
        throw error;
      }
    },
    onSuccess: () => {
      // Invalidate and refetch brands query
      queryClient.invalidateQueries({ queryKey: ['brands'] });
    }
  });
};